// © Microsoft Corporation. All rights reserved.

import { getRequestData } from "./requestData";

/**
 * Checks if the DOM is available to access and use.
 * @returns {boolean} - true if DOM is available, or false if it is not. This is typically true for
 * client side rendering and false for server side rendering.
 */
export function canUseDOM(): boolean {
    return !!(typeof window !== "undefined" && window.document && window.document.createElement);
}

/**
 * Get window.location.href in an isomorphic manner.
 */
export function getLocationHref(): string {
    return canUseDOM() ? window.location.href : getRequestData().url.href;
}

/**
 * Get window.location.hostname in an isomorphic manner.
 */
export function getLocationHostname(): string {
    return canUseDOM() ? window.location.hostname : getRequestData().url.hostname;
}

/**
 * Get window.location.host in an isomorphic manner.
 */
export function getLocationHost(): string {
    return canUseDOM() ? window.location.host : getRequestData().url.host;
}

/**
 * Get window.location.origin in an isomorphic manner.
 */
export function getLocationOrigin(): string {
    return canUseDOM() ? window.location.origin : getRequestData().url.origin;
}

/**
 * Get window.location.search in an isomorphic manner.
 */
export function getLocationSearch(): string {
    return canUseDOM() ? window.location.search : getRequestData().url.search;
}

/**
 * Get window.location.pathname in an isomorphic manner.
 */
export function getLocationPathname(): string {
    return canUseDOM() ? window.location.pathname : getRequestData().url.pathname;
}

/**
 * Get window.location.protocol in an isomorphic manner.
 */
export function getLocationProtocol(): string {
    return canUseDOM() ? window.location.protocol : getRequestData().url.protocol;
}

/**
 * Get window.location.hash in an isomorphic manner.
 */
export function getLocationHash(): string {
    return canUseDOM() ? window.location.hash : getRequestData().url.hash;
}

/**
 * Get window.document.getElementById(elementId).
 */
export function getDocumentElementById(elementId: string): HTMLElement {
    if (!canUseDOM()) {
        throw new Error("getDocumentElementById is unsupported server side. This call should be wrapped in canUseDOM().");
    }

    return window.document.getElementById(elementId);
}

/**
 * Get window.document.getDocumentElementsByTagName(elementId).
 */
export function getDocumentElementsByTagName(tagName: string): HTMLCollectionOf<Element> {
    if (!canUseDOM()) {
        throw new Error("getDocumentElementsByTagName is unsupported server side. This call should be wrapped in canUseDOM().");
    }

    return window.document.getElementsByTagName(tagName);
}

/**
 * Get localStorage in an isomorphic manner.
 * Todo (http://osgvsowi/23753462), provide actual functionality for localStorage on the server side.
 */
export function getLocalStorage(): Storage {
    return canUseDOM() ? window.localStorage : null;
}

/**
 * Get indexedDB in an isomorphic manner.
 * Todo (http://osgvsowi/23753462), provide actual functionality for indexedDB on the server side.
 */
export function getIndexedDB(): IDBFactory {
    return canUseDOM() ? window.indexedDB : null;
}

/**
 * Get window.innerWidth in an isomorphic manner.
 * Todo (http://osgvsowi/23876778), remove the need for server to know about innerWidth by limiting layout options or use CSS based solution.
 */
export function getInnerWidth(): number {
    return canUseDOM() ? window.innerWidth : getRequestData().innerWidth;
}

/**
 * Get window.innerWidth in an isomorphic manner.
 * Todo (http://osgvsowi/23876778), remove the need for server to know about innerWidth by limiting layout options or use CSS based solution.
 */
export function getInnerHeight(): number {
    return canUseDOM() ? window.innerHeight : getRequestData().innerHeight;
}

/**
 * Get window.devicePixelRatio in an isomorphic manner.
 */
export function getDevicePixelRatio(): number {
    return canUseDOM() ? window.devicePixelRatio : getRequestData().devicePixelRatio;
}

/**
 * Get CSS Grid support in an isomorphic manner
 */
export function getCssGridSupport(): boolean {
    return !!(canUseDOM() ? (window.CSS && window.CSS.supports && CSS.supports("display", "grid")) : getRequestData().canUseCssGrid);
}

/**
 * Get variable font support in an isomorphic manner
 * TODO: SSR currently does not support custom fonts.
 */
export function getVariableFontSupport(): boolean {
    if (!canUseDOM()) {
        throw new Error("SSR currently does not support custom fonts.");
    }

    return !!(window.CSS && window.CSS.supports && CSS.supports("(font-variation-settings: normal)"));
}
